import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {Button, Tooltip} from '@primer/react'
import {useRef} from 'react'

import {CommentsSidePanel} from './CommentsSidePanel'
import type {PullRequest} from '../../page-data/payloads/toolbar'
import type {PageLimits} from '../../page-data/payloads/files'
import type {AliveChannels} from '../../page-data/payloads/header'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {useUnresolvedThreadCount} from '../../stores/selectors'

export interface OpenCommentsSidePanelButtonProps {
  pageLimits: PageLimits
  pullRequest: PullRequest
  aliveChannels: AliveChannels
  tabSize?: number
}

/**
 * Renders a button that opens the comments panel
 */
export function OpenCommentsPanelButton({
  pageLimits,
  pullRequest,
  aliveChannels,
  tabSize,
}: OpenCommentsSidePanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Comments)

  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()
  const isOpen = isDockedPanelOpen
  const buttonAriaLabel = isDockedPanelOpen ? 'Close comments panel' : 'Open comments panel'

  const diffCommentCount = useUnresolvedThreadCount()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenCommentsPanelButton"
        fallback={
          <Button
            aria-label="The comments panel cannot currently be opened."
            leadingVisual={CommentDiscussionIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Tooltip ref={togglePanelRef} text={buttonAriaLabel} direction="sw" type="label">
          <Button
            aria-expanded={isOpen}
            count={diffCommentCount}
            leadingVisual={CommentDiscussionIcon}
            size="small"
            variant="invisible"
            onClick={() => {
              sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
              actions.togglePanel('comments', togglePanelRef.current)
            }}
          />
        </Tooltip>
        <CommentsSidePanel
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={pullRequest.pathName}
          pullRequestAuthorLogin={pullRequest.author.login}
          aliveChannels={aliveChannels}
          tabSize={tabSize}
          toggleSidesheetRef={togglePanelRef}
          onClose={actions.closePanel}
        />
      </ErrorBoundary>
    </div>
  )
}
